import { Controller } from "@hotwired/stimulus"
import { showFlash } from "./flash_controller"

export default class extends Controller {
  static targets = ["modal", "form"]
  static values = {
    companyId: String,
    companyRole: String
  }

  openModal(event) {
    const companyId = event.currentTarget.dataset.companyVerificationCompanyIdParam
    const companyRole = event.currentTarget.dataset.companyVerificationCompanyRoleParam
    const companyType = event.currentTarget.dataset.companyVerificationCompanyTypeParam
    const companyName = event.currentTarget.dataset.companyVerificationCompanyNameParam
    const triggerElementId = event.currentTarget.dataset.companyVerificationTriggerElementId
    
    // Store trigger element ID for later use
    this.triggerElementId = triggerElementId
    
    // For new company creation
    if (!companyId) {
      const url = `/companies/new?modal=true${companyType ? `&company_type=${companyType}` : ''}${companyName ? `&company_name=${encodeURIComponent(companyName)}` : ''}`
      
      fetch(url, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        return response.text()
      })
      .then(html => {
        this.loadForm(html)
        this._showModal()
      })
      .catch(error => {
        this.showError("Nepavyko atidaryti įmonės kūrimo formos")
      })
      return
    }
    
    this.companyIdValue = companyId
    this.companyRoleValue = companyRole
    
    fetch(`/companies/${companyId}/edit?modal=true`, {
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(response.statusText)
      }
      return response.text()
    })
    .then(html => {
      this.loadForm(html)
      this._showModal()
    })
    .catch(error => {
      this.showError("Nepavyko atidaryti įmonės redagavimo formos")
    })
  }

  _showModal() {
    this.modalTarget.showModal()
  }

  close() {
    this.modalTarget.close()
  }

  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) this.close()
  }

  handleSubmit(event) {
    event.preventDefault()
    const form = event.target
    const formData = new FormData(form)

    fetch(form.action, {
      method: form.method,
      body: formData,
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(response.statusText)
      }
      return response.json()
    })
    .then(data => {
      if (data.success) {
        // Get the company ID - either existing (from this.companyIdValue) or newly created (from data.company_id)
        const companyId = this.companyIdValue || data.company_id

        // Find and remove the badge (it's now outside company-field, next to label)
        const badge = document.querySelector(`.company-role-badge[data-company-verification-company-id-param="${companyId}"]`)
        if (badge) {
          badge.remove()
        }

        // Update company-field verified status
        const companyField = document.querySelector(`.${this.companyRoleValue}-field`)
        if (companyField) {
          companyField.dataset.verified = 'true'
        }

        // Auto-select the company in the form (works for both new and existing companies)
        const companyType = form.querySelector('[name="company[company_type]"]').value
        let companyName = ''

        if (companyType === 'organization') {
          const companyNameInput = form.querySelector('[name="company[company_name]"]')
          const legalFormInput = form.querySelector('[name="company[legal_form]"]')
          const baseName = companyNameInput?.value || ''
          const legalForm = legalFormInput?.value || ''
          companyName = legalForm ? `${baseName}, ${legalForm}` : baseName
        } else {
          const firstNameInput = form.querySelector('[name="company[first_name]"]')
          const lastNameInput = form.querySelector('[name="company[last_name]"]')
          if (firstNameInput && lastNameInput) {
            companyName = `${firstNameInput.value} ${lastNameInput.value}`.trim()
          }
        }

        // Find the company selector that triggered this modal
        // For new companies, we need to find the right selector based on context
        let companySelector = null
        
        if (this.companyRoleValue) {
          // Existing company editing - use role field
          const roleField = document.querySelector(`.${this.companyRoleValue}-field`)
          companySelector = roleField?.querySelector('[data-controller="form-company-selector"]')
        } else {
          // New company creation - try to find the specific selector that triggered this modal
          if (this.triggerElementId) {
            companySelector = document.getElementById(this.triggerElementId)
          }
          
          // Fallback: find any empty selector
          if (!companySelector) {
            const allSelectors = document.querySelectorAll('[data-controller="form-company-selector"]')
            for (let selector of allSelectors) {
              const hiddenInput = selector.querySelector('input[type="hidden"]')
              const placeholder = selector.querySelector('[data-form-company-selector-target="placeholder"]')
              // Look for empty selector with default placeholder text
              if (hiddenInput && (!hiddenInput.value || hiddenInput.value === '') && 
                  placeholder && placeholder.textContent === 'Įmonės paieška') {
                companySelector = selector
                break
              }
            }
          }
        }
        
        if (companySelector) {
          const placeholder = companySelector.querySelector('[data-form-company-selector-target="placeholder"]')
          const hiddenInput = companySelector.querySelector('input[type="hidden"]')
          
          if (placeholder && hiddenInput) {
            placeholder.textContent = companyName
            placeholder.classList.remove('text-gray-500')
            placeholder.classList.add('text-black', 'font-medium')
            hiddenInput.value = companyId
            
            // Refresh custom fields section
            const customFieldsFrame = document.getElementById('custom_fields')
            if (customFieldsFrame) {
              const currentUrl = new URL(window.location.href)
              const params = new URLSearchParams(currentUrl.search)
              params.set(hiddenInput.name.replace(/.*\[(.*)\]/, '$1'), companyId)
              
              const frameUrl = `${currentUrl.pathname}?${params.toString()}`
              customFieldsFrame.setAttribute('src', frameUrl)
            }
          }

          const field = companySelector.closest('.company-field')
          if (field) {
            field.dataset.verified = 'true'
            field.dataset.hasCompany = 'true'
          }
        }
        
        // Clean up trigger flag if it exists
        if (this.triggerElementId) {
          const triggerElement = document.getElementById(this.triggerElementId)
          if (triggerElement) {
            delete triggerElement.dataset.creatingCompany
          }
        }
        
        // Dispatch custom event for auto-selection
        document.dispatchEvent(new CustomEvent('company-created', {
          detail: {
            companyId: companyId,
            companyName: companyName,
            targetElement: this.element,
            triggerElementId: this.triggerElementId
          }
        }))

        this.refreshInvoiceNextAction()

        // If we're on the companies index page (no company selector), reload the page
        if (!companySelector) {
          window.location.reload()
        } else {
          this.close()
        }
      } else {
        this.loadForm(data.html)
      }
    })
    .catch(error => {
      this.showError("Nepavyko išsaugoti įmonės duomenų")
    })
  }

  loadForm(html) {
    this.formTarget.innerHTML = html
    this.formTarget.querySelector('form')?.addEventListener('submit', this.handleSubmit.bind(this))

    const switcher = this.formTarget.closest('[data-controller~="company-type-switcher"]')
    if (switcher) this.application.getControllerForElementAndIdentifier(switcher, 'company-type-switcher')?.sync()
  }

  refreshInvoiceNextAction() {
    const currentAction = document.getElementById('invoice-next-action')
    if (!currentAction) return

    fetch(window.location.href, {
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (!response.ok) return null
      return response.text()
    })
    .then(html => {
      if (!html) return

      const doc = new DOMParser().parseFromString(html, 'text/html')
      const nextAction = doc.getElementById('invoice-next-action')

      if (nextAction) {
        currentAction.replaceWith(nextAction)
      } else {
        currentAction.remove()
      }
    })
  }

  showError(message) {
    showFlash(message, "error")
  }
}
